import { IEvent, IOption, IFilterState } from "../interfaces";

/**
 * Defines the stateful behavior of a filter.
 */
export class FilterState<T extends IEvent> implements IFilterState<T> {
    constructor(
        public propertyToFilter: keyof T,
        public multiselect: boolean = false,
        public options: IOption[] = [],
        public selectedOptions: number[] = [],
        public loaded: boolean = false
    ) {
        this.filter = this.filter.bind(this);
    }

    /**
     * The filter's individual filter function, compatible with Array.prototype.filter()
     * 
     * @param event The event to be checked
     */
    public filter(event: T): boolean {

        // No selected options: event always passes
        if (this.selectedOptions.length === 0) {
            return true;
        }

        // get the value of the filtered property on this event
        const value = event[this.propertyToFilter];


        if (this.multiselect) {
            // different behavior for multiselect (checkboxes)...
            switch (typeof value) {
                // different behavior for arrays..
                case 'object':
                    if ('length' in value) {
                        for (let i = 0; i < (value as any).length; i++) {
                            for (let j = 0; j < this.selectedOptions.length; j++) {

                                // iterate through all possible options
                                if (value[i].toString() === this.options[this.selectedOptions[j]].value) {

                                    // if this event's values match a selected option
                                    return true;
                                }
                            }
                        }
                    }
                    break;
                // ...and strings
                case 'string':
                    for (let i = 0; i < this.selectedOptions.length; i++) {
                        if (value.toString() === this.options[this.selectedOptions[i]].value) {

                            // if this event's value matches a selected option
                            return true;
                        }
                    }
                    break;
            }
        } else {
            // ...and single-option (radios)
            switch (typeof value) {
                // different behavior for arrays..
                case 'object':
                    if ('length' in value) {
                        for (let i = 0; i < (value as any).length; i++) {

                            // iterate through all possible options
                            if (value[i].toString().toLowerCase() === this.options[this.selectedOptions[0]].value.toLowerCase()) {

                                // if this event's values match the selected option
                                return true;
                            }
                        }
                    }
                    break;
                // ...and strings
                case 'string':
                    // if this event's value matches the selected option
                    return value.toString().toLowerCase() === this.options[this.selectedOptions[0]].value.toLowerCase();
            }
        }
        // undefined behavior, returns false
        return false;
    }

    /**
     * Update the filter's options (if the date range changes).
     * Preserves state by maintaining already-selected options.
     * 
     * @param options additional options loaded from the Calendar API
     */
    public update(options: IOption[]) {

        // create a collection of references to the previously-selected options
        const oldSelectedOptions = [...this.options.filter((option, index) => this.selectedOptions.some(i => i === index))];

        // commit the new options to the filter
        this.options = [...options];
        // reset selections
        const selectedOptions: number[] = [];

        // apply previously-selected options to the new (possibly modified) order
        this.options.forEach((option, index) => {
            if (oldSelectedOptions.some(o => o.value === option.value)) {
                selectedOptions.push(index);
            }
        });

        // commit the selected options to the filter
        this.commit(selectedOptions);
    }

    /**
     * Applies a new selection to the state of the filter.
     * 
     * @param selection the updated state to commit
     */
    public commit(selection: number[]) {
        this.selectedOptions = [...selection];
    }
}
